import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  Download as DownloadIcon,
  WorkspacePremium as CertIcon,
} from '@mui/icons-material';
import { userApi, certificateApi } from '../services/api';

function CertificatesPage() {
  const { data: certificates, isLoading } = useQuery({
    queryKey: ['my-certificates'],
    queryFn: () => userApi.getCertificates().then((r) => r.data),
  });

  const handleDownload = async (id: string, name: string) => {
    try {
      const response = await certificateApi.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Meine Zertifikate</Typography>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Meine Zertifikate</Typography>

      {certificates && certificates.length > 0 ? (
        <Grid container spacing={3}>
          {certificates.map((cert: any) => {
            const isExpired = cert.expiresAt && new Date(cert.expiresAt) < new Date();

            return (
              <Grid item xs={12} md={6} key={cert.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" gap={2}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: isExpired ? 'grey.300' : 'primary.light',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CertIcon sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="h6">{cert.certificate.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cert.certificate.course?.title}
                        </Typography>
                        <Box display="flex" gap={1} mt={1}>
                          <Chip
                            size="small"
                            label={isExpired ? 'Abgelaufen' : 'Gültig'}
                            color={isExpired ? 'error' : 'success'}
                          />
                          <Chip
                            size="small"
                            label={cert.verificationCode}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Ausgestellt: {new Date(cert.issuedAt).toLocaleDateString('de-DE')}
                        {cert.expiresAt && ` | Gültig bis: ${new Date(cert.expiresAt).toLocaleDateString('de-DE')}`}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(cert.id, cert.certificate.name)}
                      >
                        PDF
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CertIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Noch keine Zertifikate
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Schließe Kurse ab, um Zertifikate zu erhalten.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default CertificatesPage;
